import {
	Box,
	IconButton,
	SvgIcon,
	Tooltip,
	useTheme,
} from "@mui/material"
import { useState, useRef, useEffect } from "react"
import {
	Sidebar,
	SidebarProps,
	ComponentTitle,
} from "components"
import * as crud from "enums/crud"
import { SharedResizeHandleStyles } from "lib/styles/resize"
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft"
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight"
import { UseResponsiveSidebar } from "lib/hooks/useResponsiveSidebar"

export function EditViewContainmentSidebar({
	mode,
	onClose,
	render,
	sidebarProps,
}: {
	mode: crud.Crud
	onClose: () => void
	render: (m: crud.Crud) => React.ReactNode
	sidebarProps?: Partial<SidebarProps>
}): React.JSX.Element | null {
	const theme = useTheme()
	const windowSize = useWindowSize()

	const [widthPercent, currentBreakpoint, setWidthPercent] = UseResponsiveSidebar(windowSize.width)

	const [isExpanded, setIsExpanded] = useState(false)

	const isDragging = useRef(false)
	const startX = useRef(0)
	const startWidth = useRef(0)

	const width = Math.floor(windowSize.width * widthPercent)

	const handleMouseDown = (e: React.MouseEvent) => {
		e.preventDefault()
		isDragging.current = true
		startX.current = e.clientX
		startWidth.current = width
		setIsExpanded(false)
	}

	useEffect(() => {
		const onMouseMove = (e: MouseEvent) => {
			if (!isDragging.current) return
			const delta = startX.current - e.clientX
			const newWidth = startWidth.current + delta
			const newPercent = newWidth / window.innerWidth
			setWidthPercent(Math.min(0.95, Math.max(0.2, newPercent)))
		}

		const onMouseUp = () => {
			isDragging.current = false
		}

		document.addEventListener("mousemove", onMouseMove)
		document.addEventListener("mouseup", onMouseUp)
		return () => {
			document.removeEventListener("mousemove", onMouseMove)
			document.removeEventListener("mouseup", onMouseUp)
		}
	}, [])


	useEffect(() => {
		if (sidebarProps?.pushContent) {
			const body = document.body
			body.style.transition = "none"
			body.style.marginRight = `${width}px`
			return () => {
				body.style.marginRight = "0"
			}
		}
	}, [width, sidebarProps?.pushContent])

	function useWindowSize() {
		const [size, setSize] = useState({
			width: window.innerWidth,
			height: window.innerHeight,
		})

		useEffect(() => {
			const onResize = () =>
				setSize({ width: window.innerWidth, height: window.innerHeight })

			window.addEventListener("resize", onResize)
			return () => window.removeEventListener("resize", onResize)
		}, [])

		return size
	}

	/* eslint-disable-next-line */
	function HorizontalSplitIcon(props: any) {
		return (
			<SvgIcon {...props}>
				<rect
					x="2"
					y="5"
					width="10"
					height="14"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
				/>
				<rect
					x="12"
					y="5"
					width="10"
					height="14"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
				/>
			</SvgIcon>
		)
	}

	return (
		<>
			<Sidebar
				width={width}
				onClose={onClose}
				pushContent
				open
				title={
					<Box mt={1} mb={1} display="flex" alignItems="center" gap={1}
						justifyContent="space-between">
						<ComponentTitle
							title={
								mode.Is("Read")
									? "Containment Details"
									: "Edit Containment"
							}
						/>
						{currentBreakpoint !== "mobile" && (
							<>
								<Tooltip title="Expand Sidebar">
									<IconButton
										onClick={() => {
											setIsExpanded(false)
											setWidthPercent(0.9)
										}}
										color={widthPercent === 0.9 ? "primary" : "default"}
									>
										<KeyboardDoubleArrowLeftIcon />
									</IconButton>
								</Tooltip>
								<Tooltip title="Narrow Sidebar">
									<IconButton
										onClick={() => {
											setIsExpanded(true)
											setWidthPercent(0.25)
										}}
										color={widthPercent === 0.25 ? "primary" : "default"}
									>
										<KeyboardDoubleArrowRightIcon />
									</IconButton>
								</Tooltip>
								<Tooltip title="Split 50/50">
									<IconButton
										onClick={() => {
											setIsExpanded(false)
											setWidthPercent(0.5)
										}}
										color={widthPercent === 0.5 ? "primary" : "default"}
									>
										<HorizontalSplitIcon />
									</IconButton>
								</Tooltip>
							</>
						)}
					</Box>
				}
				{...sidebarProps}
			>
				{render(mode)}
			</Sidebar>

			<Box
				onMouseDown={handleMouseDown}
				sx={{
					...SharedResizeHandleStyles(theme),
					position: "fixed",
					top: 0,
					right: `${width}px`,
					width: "5px",
					height: "100vh",
					cursor: "col-resize",
				}}
			/>
		</>
	)
}
